import { useState } from 'react';

import { fetcher, translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { usePush } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonKind } from 'bloko/blocks/button';
import Link from 'bloko/blocks/link';
import Loading from 'bloko/blocks/loading';
import Modal, { ModalContent, ModalFooter, ModalHeader, ModalTitle } from 'bloko/blocks/modal';
import VSpacing from 'bloko/blocks/vSpacing';

import useToggleState from 'src/hooks/useToggleState';

import styles from './styles.less';

const RESET_RESULT_URL = '/applicant-services/career_guidance';

declare global {
    interface FetcherDeleteApi {
        [RESET_RESULT_URL]: {
            queryParams: unknown;
            response: void;
        };
    }
}

const TrlKeys = {
    resetButton: 'applicantServices.career_guidance.result.reset.button',
    resetTitle: 'applicantServices.career_guidance.result.reset.title',
    resetDescription: 'applicantServices.career_guidance.result.reset.description',
    resetCancel: 'applicantServices.career_guidance.result.reset.cancel',
    resetResume: 'applicantServices.career_guidance.result.reset.resume',
    resetSave: 'applicantServices.career_guidance.result.reset.save',
    resetSaveLink: 'applicantServices.career_guidance.result.reset.saveLink',
};

enum ModalStage {
    Alert,
    Resume,
}

const Reset: TranslatedComponent<{ pdfPath?: string; stretched: boolean }> = ({ trls, pdfPath, stretched }) => {
    const [visible, toggleVisible] = useToggleState(false);
    const [stage, setStage] = useState(pdfPath ? ModalStage.Alert : ModalStage.Resume);
    const [fetching, setFetching] = useState(false);
    const push = usePush();
    const reset = () => {
        setFetching(true);
        fetcher
            .delete(RESET_RESULT_URL, { params: {} })
            .then(() => {
                push('applicant-services/payment/CAREER_GUIDANCE');
            })
            .catch((error) => console.error(error))
            .finally(() => setFetching(false));
    };
    return (
        <>
            <Button
                onClick={toggleVisible}
                kind={ButtonKind.Success}
                stretched={stretched}
                data-qa="career-guidance-reset"
            >
                {trls[TrlKeys.resetButton]}
            </Button>
            <Modal visible={visible} onClose={toggleVisible} data-qa="career-guidance-reset-modal">
                <ModalHeader>
                    <ModalTitle>{trls[TrlKeys.resetTitle]}</ModalTitle>
                </ModalHeader>
                <ModalContent>
                    <div className={styles.modalContent}>
                        {trls[TrlKeys.resetDescription]}
                        {stage === ModalStage.Resume && pdfPath && (
                            <>
                                <VSpacing base={4} />
                                <Link
                                    disableVisited
                                    href={pdfPath}
                                    target="_blank"
                                    data-qa="career-guidance-reset-download"
                                >
                                    <div className={styles.linkWrapper}>
                                        <div className={styles.linkImage} />
                                        {trls[TrlKeys.resetSaveLink]}
                                    </div>
                                </Link>
                            </>
                        )}
                    </div>
                </ModalContent>
                <ModalFooter>
                    <div className={`${styles.buttonsContainer} ${styles.buttonsContainerEnd}`}>
                        <Button stretched={stretched} onClick={toggleVisible}>
                            {trls[TrlKeys.resetCancel]}
                        </Button>
                        {stage === ModalStage.Alert && (
                            <Button
                                stretched={stretched}
                                onClick={reset}
                                disabled={fetching}
                                data-qa="career-guidance-reset-continue"
                                loading={fetching && <Loading />}
                            >
                                {trls[TrlKeys.resetResume]}
                            </Button>
                        )}
                        {stage === ModalStage.Alert && (
                            <Button
                                stretched={stretched}
                                onClick={() => setStage(ModalStage.Resume)}
                                kind={ButtonKind.Primary}
                                data-qa="career-guidance-reset-save"
                            >
                                {trls[TrlKeys.resetSave]}
                            </Button>
                        )}
                        {stage === ModalStage.Resume && (
                            <Button
                                stretched={stretched}
                                onClick={reset}
                                disabled={fetching}
                                loading={fetching && <Loading />}
                                kind={ButtonKind.Primary}
                                data-qa="career-guidance-reset-confirm"
                            >
                                {trls[TrlKeys.resetButton]}
                            </Button>
                        )}
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default translation(Reset);
